import { html, nothing, type TemplateResult } from 'lit';

// Markdown for agent text. Parsed to a small tree and rendered as Lit templates, so text never
// reaches innerHTML: raw HTML in a message stays literal.
type Inline = string | { t: 'code'; v: string } | { t: 'strong' | 'em' | 'del' | 'span'; c: Inline[] } | { t: 'a'; href: string; c: Inline[] } | { t: 'br' };
type Block =
  | { t: 'p'; c: Inline[] } | { t: 'h'; level: number; c: Inline[] } | { t: 'pre'; v: string } | { t: 'quote'; c: Block[] } | { t: 'hr' }
  | { t: 'table'; head: Inline[][]; rows: Inline[][][] } | { t: 'list'; ordered: boolean; start: number; items: Block[][] };

const safeHref = (raw: string): string | null => /^(https?:|mailto:)/i.test(raw.trim()) ? raw.trim() : null;

// Trailing punctuation that is almost never part of a URL in prose (same rule as linkify).
function trimUrl(url: string): string {
  const count = (s: string, ch: string) => s.split(ch).length - 1;
  for (;;) {
    const c = url[url.length - 1];
    if (c && '.,!?;:'.includes(c) || c === ')' && count(url, ')') > count(url, '(') || c === ']' && count(url, ']') > count(url, '[')) { url = url.slice(0, -1); continue; }
    return url;
  }
}

const INLINE: { re: RegExp; make: (m: RegExpExecArray) => Inline }[] = [
  { re: /^`([^`]+)`/, make: m => ({ t: 'code', v: m[1]! }) },
  { re: /^!\[([^\]]*)\]\(([^)\s]+)(?:\s+"[^"]*")?\)/, make: m => { const href = safeHref(m[2]!); return href ? { t: 'a', href, c: [m[1] || href] } : m[1]!; } },
  { re: /^\[((?:[^[\]]|\[[^\]]*\])+)\]\(([^)\s]+)(?:\s+"[^"]*")?\)/, make: m => { const href = safeHref(m[2]!); return href ? { t: 'a', href, c: inline(m[1]!) } : { t: 'span', c: inline(m[1]!) }; } },
  { re: /^<(https?:\/\/[^>\s]+)>/, make: m => ({ t: 'a', href: m[1]!, c: [m[1]!] }) },
  { re: /^(\*\*|__)(?=\S)([\s\S]*?\S)\1/, make: m => ({ t: 'strong', c: inline(m[2]!) }) },
  { re: /^~~(?=\S)([\s\S]*?\S)~~/, make: m => ({ t: 'del', c: inline(m[1]!) }) },
  { re: /^\*(?=[^\s*])((?:[^*]|\*\*[^*]+\*\*)*?[^\s*])\*/, make: m => ({ t: 'em', c: inline(m[1]!) }) },
  { re: /^_(?=[^\s_])([^_]*?[^\s_])_(?![A-Za-z0-9])/, make: m => ({ t: 'em', c: inline(m[1]!) }) },
];

function inline(text: string): Inline[] {
  const out: Inline[] = [];
  let buf = '', i = 0;
  const flush = () => { if (buf) { out.push(buf); buf = ''; } };
  while (i < text.length) {
    const rest = text.slice(i), ch = text[i]!;
    if (ch === '\\' && /^[\\`*_{}[\]()#+\-.!|~>]/.test(text[i + 1] ?? '')) { buf += text[i + 1]; i += 2; continue; }
    // `_` only opens emphasis at a word start, so snake_case identifiers stay intact.
    if ('`![<*_~'.includes(ch) && (ch !== '_' || i === 0 || !/[A-Za-z0-9]/.test(text[i - 1]!))) {
      const hit = INLINE.map(r => [r, r.re.exec(rest)] as const).find(([, m]) => m);
      if (hit) { flush(); out.push(hit[0].make(hit[1]!)); i += hit[1]![0].length; continue; }
    }
    const url = /^https?:\/\/[^\s<>"'`]+/.exec(rest);
    if (url && (i === 0 || /[\s(]/.test(text[i - 1]!))) {
      const u = trimUrl(url[0]);
      try { new URL(u); flush(); out.push({ t: 'a', href: u, c: [u] }); i += u.length; continue; } catch { /* not a URL */ }
    }
    if (ch === '\n') { flush(); out.push({ t: 'br' }); i += 1; continue; }
    buf += ch; i += 1;
  }
  flush();
  return out;
}

const LIST_ITEM = /^(\s*)([-*+]|\d{1,9}[.)])\s+(.*)$/;
const TABLE_SEP = /^\s*\|?\s*:?-{2,}:?\s*(\|\s*:?-{2,}:?\s*)*\|?\s*$/;
const HR = /^\s*([-*_])(\s*\1){2,}\s*$/;
const indentOf = (s: string) => /^\s*/.exec(s)![0].replace(/\t/g, '    ').length;

function splitRow(line: string): string[] {
  const s = line.trim().replace(/^\|/, '').replace(/\|$/, '');
  const cells: string[] = [];
  let cur = '';
  for (let i = 0; i < s.length; i++) {
    if (s[i] === '\\' && s[i + 1] === '|') { cur += '|'; i++; } else if (s[i] === '|') { cells.push(cur.trim()); cur = ''; } else cur += s[i];
  }
  cells.push(cur.trim());
  return cells;
}

const isTable = (line: string, next?: string) => line.includes('|') && next !== undefined && TABLE_SEP.test(next);
const isBlockStart = (line: string, next?: string) =>
  /^\s*(```|~~~)/.test(line) || /^#{1,6}\s/.test(line) || /^\s*>/.test(line) || LIST_ITEM.test(line) || HR.test(line) || isTable(line, next);

function blocks(lines: string[]): Block[] {
  const out: Block[] = [];
  let i = 0;
  while (i < lines.length) {
    const line = lines[i]!;
    if (!line.trim()) { i++; continue; }
    const fence = /^\s*(```|~~~)/.exec(line);
    if (fence) {
      const code: string[] = [];
      i++;
      while (i < lines.length && !lines[i]!.trim().startsWith(fence[1]!)) code.push(lines[i++]!);
      i++;
      out.push({ t: 'pre', v: code.join('\n') });
      continue;
    }
    const h = /^(#{1,6})\s+(.*?)\s*#*\s*$/.exec(line);
    if (h) { out.push({ t: 'h', level: h[1]!.length, c: inline(h[2]!) }); i++; continue; }
    if (HR.test(line)) { out.push({ t: 'hr' }); i++; continue; }
    if (/^\s*>/.test(line)) {
      const quote: string[] = [];
      while (i < lines.length && /^\s*>/.test(lines[i]!)) quote.push(lines[i++]!.replace(/^\s*>\s?/, ''));
      out.push({ t: 'quote', c: blocks(quote) });
      continue;
    }
    if (isTable(line, lines[i + 1])) {
      const head = splitRow(line), rows: string[][] = [];
      i += 2;
      while (i < lines.length && lines[i]!.includes('|') && lines[i]!.trim()) rows.push(splitRow(lines[i++]!));
      out.push({ t: 'table', head: head.map(inline), rows: rows.map(r => head.map((_, j) => inline(r[j] ?? ''))) });
      continue;
    }
    if (LIST_ITEM.test(line)) { i = list(lines, i, out); continue; }
    const para = [line];
    i++;
    while (i < lines.length && lines[i]!.trim() && !isBlockStart(lines[i]!, lines[i + 1])) para.push(lines[i++]!);
    out.push({ t: 'p', c: inline(para.join('\n').trim()) });
  }
  return out;
}

function list(lines: string[], i: number, out: Block[]): number {
  const first = LIST_ITEM.exec(lines[i]!)!, base = indentOf(lines[i]!), ordered = /\d/.test(first[2]!);
  const node: Block = { t: 'list', ordered, start: ordered ? parseInt(first[2]!, 10) : 1, items: [] };
  while (i < lines.length) {
    const m = LIST_ITEM.exec(lines[i]!);
    if (!m || indentOf(lines[i]!) !== base || /\d/.test(m[2]!) !== ordered) break;
    const body = [m[3]!];
    i++;
    // Continuation lines and nested lists belong to this item while they are indented past its marker.
    while (i < lines.length) {
      const l = lines[i]!;
      if (!l.trim()) {
        if (i + 1 < lines.length && lines[i + 1]!.trim() && indentOf(lines[i + 1]!) > base) { body.push(''); i++; continue; }
        break;
      }
      if (indentOf(l) > base) { body.push(l.slice(Math.min(indentOf(l), base + 2))); i++; continue; }
      if (!LIST_ITEM.test(l) && !isBlockStart(l, lines[i + 1])) { body.push(l); i++; continue; }
      break;
    }
    node.items.push(blocks(body));
    while (i + 1 < lines.length && !lines[i]!.trim() && LIST_ITEM.test(lines[i + 1]!) && indentOf(lines[i + 1]!) === base) i++;
  }
  out.push(node);
  return i;
}

export function parseMarkdown(text: string): Block[] {
  return blocks(text.replace(/\r\n?/g, '\n').split('\n'));
}

function renderInline(nodes: Inline[]): unknown[] {
  return nodes.map(n => {
    if (typeof n === 'string') return n;
    switch (n.t) {
      case 'code': return html`<code>${n.v}</code>`;
      case 'strong': return html`<strong>${renderInline(n.c)}</strong>`;
      case 'em': return html`<em>${renderInline(n.c)}</em>`;
      case 'del': return html`<del>${renderInline(n.c)}</del>`;
      case 'span': return renderInline(n.c);   // a link with an unsafe target keeps only its text
      case 'br': return html`<br>`;
      case 'a': return /^https?:/i.test(n.href)
        ? html`<a href=${n.href} target="_blank" rel="noopener noreferrer">${renderInline(n.c)}</a>`
        : html`<a href=${n.href}>${renderInline(n.c)}</a>`;
    }
  });
}

function renderBlock(b: Block): TemplateResult | typeof nothing {
  switch (b.t) {
    case 'p': return html`<p>${renderInline(b.c)}</p>`;
    case 'h': {
      const c = renderInline(b.c);
      return [html`<h1>${c}</h1>`, html`<h2>${c}</h2>`, html`<h3>${c}</h3>`, html`<h4>${c}</h4>`, html`<h5>${c}</h5>`, html`<h6>${c}</h6>`][b.level - 1]!;
    }
    case 'pre': return html`<pre><code>${b.v}</code></pre>`;
    case 'quote': return html`<blockquote>${b.c.map(renderBlock)}</blockquote>`;
    case 'hr': return html`<hr>`;
    case 'table': return html`<div class="table"><table><thead><tr>${b.head.map(c => html`<th>${renderInline(c)}</th>`)}</tr></thead><tbody>${b.rows.map(r => html`<tr>${r.map(c => html`<td>${renderInline(c)}</td>`)}</tr>`)}</tbody></table></div>`;
    case 'list': {
      // A single-paragraph item renders inline, without a wrapping <p>.
      const items = b.items.map(item => html`<li>${item.map((x, k) => k === 0 && x.t === 'p' ? renderInline(x.c) : renderBlock(x))}</li>`);
      return b.ordered ? html`<ol start=${b.start === 1 ? nothing : b.start}>${items}</ol>` : html`<ul>${items}</ul>`;
    }
  }
}

export function renderMarkdown(text: string): TemplateResult {
  return html`<div class="md">${parseMarkdown(text).map(renderBlock)}</div>`;
}
